import React, { useState, useEffect, useRef } from 'react';
import { Container, Text, Title, Paper, Button, Select, Group, ActionIcon, Menu, Box, Drawer, Stack } from '@mantine/core';
import { IconClock, IconLanguage } from '@tabler/icons-react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useInterval } from './hooks/useInterval';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import LessonMarkPanel from './LessonMarkPanel';

const API_BASE_URL = window.location.hostname === 'alt-service.lingva-pro.org'
  ? window.location.origin + '/api'
  : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api');
const BBB_HOST = window.location.hostname === 'alt-service.lingva-pro.org' ? 'alt.lingva-pro.org' : 'class.lingva-pro.org';

function TeacherPage() {
  const { teacherId } = useParams();
  const [lessons, setLessons] = useState([]);
  const [isCallActive, setIsCallActive] = useState(false);
  const [timezone, setTimezone] = useState(() => {
    return localStorage.getItem('timezone') || 'America/New_York';
  });
  const [loadingLessonId, setLoadingLessonId] = useState(null);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || (navigator.language.startsWith('ru') ? 'Русский' : 'English');
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const { t, i18n } = useTranslation();

  const handleTimezoneChange = (value) => {
    if (!value) return;
    setTimezone(value);
    localStorage.setItem('timezone', value);
  };

  const handleLanguageChange = (value) => {
    if (!value) return;
    setLanguage(value);
    localStorage.setItem('language', value);
    window.location.reload();
  };

  const timezones = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo',
    'Australia/Sydney', 'Australia/Perth', 'Pacific/Auckland', 'Pacific/Honolulu',
    'Africa/Cairo', 'Africa/Johannesburg', 'America/Sao_Paulo', 'America/Mexico_City',
    'Asia/Singapore', 'Asia/Seoul', 'Europe/Rome', 'Europe/Madrid'
  ];

  const timezoneData = timezones.map(tz => ({ value: tz, label: tz.replace('_', ' ') }));
  const languageData = [
    { value: 'Русский', label: 'Русский' },
    { value: 'English', label: 'English' },
  ];

  const isFetchingLessons = useRef(false);

  const fetchLessons = async () => {
    if (isFetchingLessons.current) return;
    isFetchingLessons.current = true;

    try {
      const response = await fetch(`${API_BASE_URL}/teacher/${teacherId}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        console.error(`Ошибка HTTP: ${response.status}`);
        return;
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error('Некорректный формат данных:', data);
        return;
      }

      if (data.length > 0) {
        setLessons(data);
        setIsCallActive(true);
      } else {
        setLessons([]);
        setIsCallActive(false);
      }
    } catch (error) {
      console.error('Ошибка при получении данных об уроках:', error);
    } finally {
      isFetchingLessons.current = false;
    }
  };

  useEffect(() => {
    fetchLessons();
  }, [teacherId]);

  useInterval(() => {
    fetchLessons();
  }, 300000);

  // Панель «Отметить урок» появляется в карточке с началом урока — пересчитываем раз в 30 с
  useInterval(() => {
    setNow(Date.now());
  }, 30000);

  const hasLessonStarted = (dateStart) => {
    const start = DateTime.fromISO(dateStart.replace(' ', 'T'), { zone: 'utc' });
    return start.isValid && start.toMillis() <= now;
  };

  const formatDate = (utcDate, timezone) => {
    const isoDate = utcDate.replace(' ', 'T');
    const date = DateTime.fromISO(isoDate, { zone: 'utc' }).setZone(timezone);
    if (!date.isValid) {
      return t('invalidDate');
    }
    return date.setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM yyyy, HH:mm');
  };

  const handleJoinLesson = async (lesson) => {
    setLoadingLessonId(lesson.id);
    try {
      const response = await fetch(`${API_BASE_URL}/createroom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ create: lesson.CreateLink }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      const result = await response.text();
      if (result === 'OK') {
        window.location.href = lesson.teacherLink;
      } else {
        console.error('Ошибка создания комнаты:', result);
        alert(t('roomCreationError'));
      }
    } catch (error) {
      console.error('Ошибка при создании комнаты:', error.message);
      alert(t('roomCreationError'));
    } finally {
      setLoadingLessonId(null);
    }
  };

  const calculateTimeLeft = (dateStart) => {
    const isoDateStart = dateStart.replace(' ', 'T');
    const now = DateTime.now().setZone('utc');
    const lessonTime = DateTime.fromISO(isoDateStart, { zone: 'utc' });

    if (!lessonTime.isValid) {
      console.error(`Некорректный формат dateStart: ${dateStart}`);
      return { text: t('invalidDate'), time: { hours: 0, minutes: 0, seconds: 0 } };
    }

    const diff = lessonTime.diff(now, ['hours', 'minutes', 'seconds']).toObject();

    if (diff.hours < 0 || diff.minutes < 0 || diff.seconds < 0) {
      return {
        text: t('lessonStarted'),
        time: lessonTime.diff(now, ['hours', 'minutes', 'seconds']).negate().toObject(),
      };
    }

    return {
      text: t('lessonCountdown'),
      time: diff,
    };
  };

  const LessonCountdown = ({ dateStart }) => {
    const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(dateStart));

    useInterval(() => {
      setTimeLeft(calculateTimeLeft(dateStart));
    }, 1000);

    const { text, time } = timeLeft;

    return (
      <div style={{ textAlign: 'right', marginLeft: 20 }}>
        <Text component="span" c="var(--text-muted)" size="sm">
          {text}
        </Text>
        <Text component="span" c="var(--text-muted)" size="sm" ml={5}>
          {`${Math.abs(Math.floor(time.hours || 0))} ${t('hourShort')} ${Math.abs(Math.floor(time.minutes || 0))} ${t('minuteShort')} ${Math.abs(Math.floor(time.seconds || 0))} ${t('secondShort')}`}
        </Text>
      </div>
    );
  };

  return (
    <div style={{ backgroundColor: '#f0f9ff', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <Container size="xl">
          <Group h={64} justify="space-between">
            <Group gap="sm">
              <img src="/logo.png" alt="LingvaPro Logo" style={{ height: 40 }} />
              <Title order={4} c="#0369A1" fw={700}>
                {t('teacher')}
              </Title>
            </Group>
            <Group gap="sm" visibleFrom="md">
              <Select
                data={timezoneData}
                value={timezone}
                onChange={handleTimezoneChange}
                size="sm"
                styles={{ input: { height: 36, minHeight: 36 } }}
              />
              <IconLanguage size={20} style={{ opacity: 0.6 }} />
              <Select
                data={languageData}
                value={language}
                onChange={handleLanguageChange}
                size="sm"
                styles={{ input: { height: 36, minHeight: 36 } }}
              />
            </Group>
            <Box hiddenFrom="md">
              <Menu
                shadow="md"
                width={260}
                opened={mobileMenuOpened}
                onChange={setMobileMenuOpened}
              >
                <Menu.Target>
                  <ActionIcon variant="subtle" size="lg" color="dark">
                    <IconLanguage size={20} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown p="sm">
                  <Stack gap="sm">
                    <Select
                      data={timezoneData}
                      value={timezone}
                      onChange={handleTimezoneChange}
                      size="sm"
                    />
                    <Select
                      data={languageData}
                      value={language}
                      onChange={handleLanguageChange}
                      size="sm"
                    />
                  </Stack>
                </Menu.Dropdown>
              </Menu>
            </Box>
          </Group>
        </Container>
      </header>

      {/* Drawer (kept for settings) */}
      <Drawer
        opened={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        position="right"
        title={t('settings')}
        padding="lg"
        size={280}
      >
        <Stack gap="lg">
          <div>
            <Text mb="xs">{t('timezone')}</Text>
            <Select
              data={timezoneData}
              value={timezone}
              onChange={handleTimezoneChange}
            />
          </div>
          <div>
            <Text mb="xs">{t('language')}</Text>
            <Select
              data={languageData}
              value={language}
              onChange={handleLanguageChange}
            />
          </div>
        </Stack>
      </Drawer>

      <Container size="xl">
        {isCallActive && lessons.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 20 }}>
            {lessons.map((lesson, idx) => (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
              >
                <Paper className="glass-card" p="lg" style={{ cursor: 'default' }}>
                  <div className="teacher-lesson-inner">
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <Title order={4} fw={700}>
                        {t('lessonScheduledStudent', {
                          lessonDate: formatDate(lesson.dateStart, timezone),
                          // В нике зашито направление обучения: «Sergei (ASVAB321)»
                          studentName: lesson.studentNickname
                            ? `${lesson.studentName} (${lesson.studentNickname})`
                            : lesson.studentName,
                        })}
                      </Title>
                      <Group gap="sm" mt="sm">
                        <IconClock size={18} color="var(--primary)" />
                        <Text size="sm" fw={600} c="var(--text-primary)">
                          {`${Math.floor(lesson.duration / 60)} ${t('minuteMany')}`}
                        </Text>
                        <LessonCountdown dateStart={lesson.dateStart} />
                      </Group>
                    </div>
                    <div className="teacher-join-btn">
                      <Button
                        className="gradient-btn"
                        size="lg"
                        onClick={() => handleJoinLesson(lesson)}
                        loading={loadingLessonId === lesson.id}
                      >
                        {t('joinLesson')}
                      </Button>
                    </div>
                  </div>
                  {hasLessonStarted(lesson.dateStart) && (
                    <LessonMarkPanel apiBaseUrl={API_BASE_URL} teacherId={teacherId} lessonId={lesson.id} />
                  )}
                </Paper>
              </motion.div>
            ))}
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height: 100,
              border: '2px dashed rgba(14, 165, 233, 0.3)',
              borderRadius: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 20,
            }}
          >
            <Text c="var(--text-muted)">
              {t('lessonEntryPlaceholder')}
            </Text>
          </div>
        )}
      </Container>

      {/* Footer */}
      <div style={{ height: 80 }} />
      <Text ta="center" size="xs" c="var(--primary)" mb="sm">
        <RouterLink
          to={`/teacher/${teacherId}/guide`}
          style={{ textDecoration: 'none', color: 'var(--primary)' }}
        >
          {t('platformInstructions')}
        </RouterLink>
        {' | '}
        <a
          href={`https://${BBB_HOST}/quality/teacher/${teacherId}?lg=${i18n.language}`}
          style={{ textDecoration: 'none', color: 'var(--primary)' }}
        >
          {t('videoConnectionTest')}
        </a>
      </Text>

      <style>
        {`
          .teacher-lesson-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }
          @media (min-width: 768px) {
            .teacher-lesson-inner {
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            }
          }
          .teacher-join-btn {
            margin-top: 20px;
            align-self: center;
          }
          @media (min-width: 768px) {
            .teacher-join-btn {
              margin-top: 0;
              margin-left: 20px;
            }
          }
        `}
      </style>
    </div>
  );
}

export default TeacherPage;
